// src/pages/Menu.js
import React from "react";
import ControlsBar from "../components/menu/ControlsBar";
import MenuTable from "../components/menu/MenuTable";
import Pagination from "../components/menu/Pagination";
import AddEditModal from "../components/menu/AddEditModal";
import {
  fetchMenuItems,
  bulkUpdateMenu,
  updateMenuItem,
  addMenuItem,
  fetchMenuItemDetails,
  fetchKOTLocations,
} from "../services/menu.service";
import { getUser } from "../lib/authToken";
import { DEV_USER } from "../config/localAuth";

const normalize = (arr) =>
  arr.map((r, i) => ({ uid: `${r.item_id}::${i}`, ...r }));

export default function Menu() {
  const user = getUser() || DEV_USER;
  const ORG_ID = user.restaurant_id;
  const LOCATION = user.location;

  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [base, setBase] = React.useState([]);
  const [items, setItems] = React.useState([]);
  const [kotList, setKotList] = React.useState([]);

  const [search, setSearch] = React.useState("");
  const [availFilter, setAvailFilter] = React.useState("all");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);

  const [pendingAvail, setPendingAvail] = React.useState(() => new Map());
  const [pendingGst, setPendingGst] = React.useState(() => new Map());
  const [unsaved, setUnsaved] = React.useState(false);

  const [ui, setUi] = React.useState({
    isAddOpen: false,
    isEditOpen: false,
    editingUid: null,
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const list = await fetchMenuItems({ orgId: ORG_ID, location: LOCATION });
      const norm = normalize(list || []);
      setBase(norm);
      setItems(norm);
      setPendingAvail(new Map());
      setPendingGst(new Map());
      setUnsaved(false);
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  }, [ORG_ID, LOCATION]);

  // Load KOT locations
  const loadKOTLocations = React.useCallback(async () => {
    try {
      const kots = await fetchKOTLocations({ orgId: ORG_ID });
      setKotList(kots || []);
    } catch (e) {
      console.warn("Failed to load KOT locations:", e);
      // Use empty array as fallback
      setKotList([]);
    }
  }, [ORG_ID]);

  React.useEffect(() => {
    load();
    loadKOTLocations();
  }, [load, loadKOTLocations]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    let filteredItems = items;

    // Apply search filter
    if (q) {
      filteredItems = filteredItems.filter((it) => {
        const a = (it.item_name || "").toLowerCase();
        const b = (it.category || "").toLowerCase();
        const c = (it.subcategory || "").toLowerCase();
        return a.includes(q) || b.includes(q) || c.includes(q);
      });
    }

    // Apply availability filter
    // Use original API value for filtering, not pending value
    // This keeps items visible when toggling within a filter
    if (availFilter !== "all") {
      filteredItems = filteredItems.filter((it) => {
        const availValue = !!it.available;
        return availFilter === "available" ? availValue : !availValue;
      });
    }

    return filteredItems;
  }, [items, search, availFilter]);

  const total = filtered.length;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  React.useEffect(() => {
    if (page > lastPage) setPage(lastPage);
  }, [lastPage, page]);
  const start = (page - 1) * pageSize;
  const pageItems = filtered.slice(start, start + pageSize);

  const rowsForTable = pageItems.map((it) => {
    const hasAvail = pendingAvail.has(it.uid);
    const availValue = hasAvail ? pendingAvail.get(it.uid) : !!it.available;
    const hasGst = pendingGst.has(it.uid);
    const gstValue = hasGst ? pendingGst.get(it.uid) : !!it.is_gst;
    const isDirty = hasAvail || hasGst;
    return { ...it, availValue, gstValue, isDirty };
  });

  // Extract unique categories from menu items
  const categories = React.useMemo(() => {
    const cats = new Set(base.map((it) => it.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [base]);

  const onSearchChange = (q) => {
    setSearch(q);
    setPage(1);
  };

  const onAvailFilterChange = (filter) => {
    setAvailFilter(filter);
    setPendingAvail(new Map());
    setPage(1);
  };

  // Track recently toggled items to prevent duplicate calls
  const [recentToggles, setRecentToggles] = React.useState(() => new Set());

  const onToggleAvailable = (uid, nextVal) => {
    // Prevent duplicate calls within 200ms window
    if (recentToggles.has(uid)) return;
    setRecentToggles((prev) => new Set([...prev, uid]));
    setTimeout(() => {
      setRecentToggles((prev) => {
        const next = new Set(prev);
        next.delete(uid);
        return next;
      });
    }, 200);

    setPendingAvail((prev) => {
      const m = new Map(prev);
      m.set(uid, !!nextVal);
      return m;
    });
    setUnsaved(true);
  };

  const onGstToggleAvailable = (uid, nextVal) => {
    // Prevent duplicate calls within 200ms window
    if (recentToggles.has(uid)) return;
    setRecentToggles((prev) => new Set([...prev, uid]));
    setTimeout(() => {
      setRecentToggles((prev) => {
        const next = new Set(prev);
        next.delete(uid);
        return next;
      });
    }, 200);

    setPendingGst((prev) => {
      const m = new Map(prev);
      m.set(uid, !!nextVal);
      return m;
    });
    setUnsaved(true);
  };

  const onEdit = async (item_id) => {
    console.log("Edit clicked:", item_id);

    try {
      const res = await fetchMenuItemDetails({
        item_id,
        orgId: ORG_ID,
      });

      console.log("API HIT SUCCESS:", res);

      // update the item in state with API data
      setItems((prev) =>
        prev.map((it) =>
          it.item_id === item_id
            ? {
                ...it,
                item_name: res.name,
                price: res.price,
                category: res.category,
                available: !!res.is_available,
                is_gst: !!res.is_gst,
                kot_ip: res.kot_ip || "",
                image_url: res.image_url || "",
              }
            : it
        )
      );

      // open edit modal (USING EXISTING UI STATE)
      const edited = items.find((it) => it.item_id === item_id);
      if (edited) {
        setUi({
          isAddOpen: false,
          isEditOpen: true,
          editingUid: edited.uid,
        });
      }
    } catch (err) {
      console.error("Edit API failed", err);
    }
  };

  const onAdd = () => setUi((u) => ({ ...u, isAddOpen: true }));
  const closeModals = () =>
    setUi({ isAddOpen: false, isEditOpen: false, editingUid: null });

  const saveAdd = async (values) => {
    try {
      setLoading(true);
      setErr(null);
      await addMenuItem(values, { orgId: ORG_ID, location: LOCATION });
      await load();
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
      closeModals();
    }
  };

  const saveEdit = async (values) => {
    const row = items.find((it) => it.uid === ui.editingUid);
    if (!row) {
      closeModals();
      return;
    }

    const priceNum = Number(values.price ?? row.price);
    const availVal =
      typeof values.available === "boolean"
        ? values.available
        : !!row.available;
    const gstVal =
      typeof values.is_gst === "boolean" ? values.is_gst : !!row.is_gst;
    const patch = {
      item_id: row.item_id,
      item_name: String(values.item_name ?? row.item_name).trim(),
      category: String(values.category ?? row.category).trim(),
      price: Number.isNaN(priceNum) ? row.price : priceNum,
      available: !!availVal,
      is_gst: !!gstVal,
      ...(values.kot_ip !== undefined ? { kot_ip: values.kot_ip } : {}),
      ...(values.image_url !== undefined ? { image_url: values.image_url } : {}),
    };

    try {
      setLoading(true);
      setErr(null);
      await updateMenuItem({ ...patch, orgId: ORG_ID, location: LOCATION });
      await load();
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
      closeModals();
    }
  };

  const onDeleteRow = async (uid) => {
    if (!window.confirm("Delete this item?")) return;
    setItems((prev) => prev.filter((it) => it.uid !== uid));
    setPendingAvail((prev) => {
      const m = new Map(prev);
      m.delete(uid);
      return m;
    });
    setPendingGst((prev) => {
      const m = new Map(prev);
      m.delete(uid);
      return m;
    });
    setUnsaved(true);
  };

  const applySubmit = async () => {
    const baseByUid = new Map(base.map((b) => [b.uid, b]));
    const patches = [];

    for (const it of items) {
      const prev = baseByUid.get(it.uid);
      if (!prev) continue;

      const nextAvail = pendingAvail.has(it.uid)
        ? pendingAvail.get(it.uid)
        : !!prev.available;

      const nextGst = pendingGst.has(it.uid)
        ? pendingGst.get(it.uid)
        : !!prev.is_gst;

      const availChanged = nextAvail !== prev.available;
      const gstChanged = nextGst !== !!prev.is_gst;
      const priceNow = Number(it.price);
      const pricePrev = Number(prev.price);
      const priceChanged = Number.isFinite(priceNow) && priceNow !== pricePrev;

      if (availChanged || gstChanged || priceChanged) {
        patches.push({
          item_id: it.item_id,
          available: !!nextAvail,
          is_gst: !!nextGst,
          price: Number.isFinite(priceNow) ? priceNow : pricePrev,
          kot_ip: it.kot_ip || "",
        });
      }
    }

    if (!patches.length) return;

    try {
      setLoading(true);
      setErr(null);
      await bulkUpdateMenu(patches, { orgId: ORG_ID, location: LOCATION });
      await load();
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  };

  const discardChanges = () => {
    setPendingAvail(new Map());
    setPendingGst(new Map());
    setUnsaved(false);
  };

  return (
    <div className="page page--menu">
      <ControlsBar
        search={search}
        onSearchChange={onSearchChange}
        availFilter={availFilter}
        onAvailFilterChange={onAvailFilterChange}
        onAdd={onAdd}
      />

      {err && (
        <div className="error-box">
          <b>Couldn't load/update menu</b>
          <div className="muted">{String(err.message || err)}</div>
        </div>
      )}

      <MenuTable
        rows={rowsForTable}
        onToggleAvailable={onToggleAvailable}
        onGstToggleAvailable={onGstToggleAvailable}
        onEdit={onEdit}
        onDelete={onDeleteRow}
        isBusy={loading}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />

      {ui.isAddOpen && (
        <AddEditModal 
          mode="add" 
          categories={categories} 
          kotList={kotList}
          onCancel={closeModals} 
          onSave={saveAdd} 
        />
      )}
      {ui.isEditOpen && (
        <AddEditModal
          mode="edit"
          initial={items.find((it) => it.uid === ui.editingUid)}
          categories={categories}
          kotList={kotList}
          onCancel={closeModals}
          onSave={saveEdit}
        />
      )}

      <div className="menu-submit">
        <button
          type="button"
          className="btn"
          disabled={
            !unsaved && pendingAvail.size === 0 && pendingGst.size === 0
          }
          onClick={discardChanges}
        >
          Discard
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={
            (!unsaved && pendingAvail.size === 0 && pendingGst.size === 0) ||
            loading
          }
          onClick={applySubmit}
          title={
            pendingAvail.size + pendingGst.size
              ? `Apply ${pendingAvail.size + pendingGst.size} change(s)`
              : "No changes"
          }
        >
          {loading ? "Saving..." : "Submit"}
        </button>
      </div>
    </div>
  );
}

